/**
 * Engraving Core Module
 * Centralized logic for engraving management, cart API, and validation
 */

window.EngravingCore = window.EngravingCore || class EngravingCore {
  constructor() {
    this.engravingServiceProduct = null;
    this.maxCharacters = 100;
    this.defaultFont = 'Bodoni MT';
    this.init();
  }

  /**
   * Initialize the engraving core
   */
  async init() {
    await this.loadEngravingServiceProduct();
  }

  /**
   * Load engraving service product from settings
   */
  async loadEngravingServiceProduct() {
    try {
      // First try to get from page if already loaded
      const existingConfig = document.querySelector('#engraving-service-config');
      if (existingConfig) {
        const configText = existingConfig.textContent.trim();
        if (configText) {
          this.engravingServiceProduct = JSON.parse(configText);
          return;
        }
      }

      // Otherwise fetch it
      const response = await fetch('/?section_id=engraving-service-config');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const configScript = doc.querySelector('#engraving-service-config');
      
      if (configScript) {
        const configText = configScript.textContent.trim();
        if (configText) {
          try {
            const config = JSON.parse(configText);
            // Log the config for debugging
            console.log('🔍 Engraving service config loaded:', config);
            // Only set if we have valid product data
            if (config.product_id && config.variant_id) {
              this.engravingServiceProduct = config;
              console.log('✅ Engraving service product configured:', {
                product_id: config.product_id,
                variant_id: config.variant_id,
                title: config.title
              });
            } else {
              console.warn('⚠️ Engraving service product not properly configured:', {
                product_id: config.product_id,
                variant_id: config.variant_id,
                title: config.title,
                message: 'Missing product_id or variant_id. Please check Theme Settings → Engraving → Engraving product is set and has an available variant.'
              });
              this.engravingServiceProduct = null;
            }
          } catch (parseError) {
            console.error('❌ Failed to parse engraving service config JSON:', parseError);
            console.error('Config text:', configText);
            this.engravingServiceProduct = null;
          }
        } else {
          console.warn('Engraving service product config is empty');
          this.engravingServiceProduct = null;
        }
      } else {
        console.warn('Engraving service product not configured - config script not found');
        this.engravingServiceProduct = null;
      }
    } catch (error) {
      console.error('Failed to load engraving service product:', error);
      // Set to null so we don't keep trying
      this.engravingServiceProduct = null;
    }
  }

  /**
   * Get engraving configuration for a product
   * @param {number} productId - Product ID
   * @returns {Object} Engraving configuration
   */
  getProductConfig(productId) {
    return window.engravingConfig?.[productId] || { blades: [], enabled: false };
  }

  /**
   * Validate engraving data
   * @param {Object} engraving - Engraving object
   * @returns {Object} { valid: boolean, error: string }
   */
  validateEngraving(engraving) {
    if (!engraving.blade || !engraving.side) {
      return { valid: false, error: 'Blade and side are required' };
    }

    if (!engraving.text || engraving.text.trim().length === 0) {
      return { valid: false, error: 'Engraving text is required' };
    }

    if (engraving.text.length > this.maxCharacters) {
      return { 
        valid: false, 
        error: `Text must be ${this.maxCharacters} characters or less` 
      };
    }

    return { valid: true, error: null };
  }

  /**
   * Format engraving for storage
   * @param {Object} engraving - Engraving object
   * @returns {Object} Formatted engraving
   */
  formatEngraving(engraving) {
    return {
      blade: parseInt(engraving.blade),
      side: engraving.side.toLowerCase(),
      text: engraving.text.trim(),
      font: engraving.font || this.defaultFont
    };
  }

  /**
   * Parse engravings from cart item properties
   * @param {Object} properties - Cart item properties
   * @returns {Array} Array of engraving objects
   */
  parseEngravingsFromProperties(properties) {
    if (!properties) {
      return [];
    }

    // First try JSON_ENGRAVING format (hidden property)
    if (properties.JSON_ENGRAVING !== undefined) {
      try {
        const jsonEngraving = typeof properties.JSON_ENGRAVING === 'string' 
          ? JSON.parse(properties.JSON_ENGRAVING) 
          : properties.JSON_ENGRAVING;
        
        if (Array.isArray(jsonEngraving) && jsonEngraving.length > 0) {
          // Convert JSON_ENGRAVING format back to internal format
          const engravings = [];
          for (const item of jsonEngraving) {
            if (item.surfaces && Array.isArray(item.surfaces)) {
              for (const surface of item.surfaces) {
                // Parse surface name like "Blade 2 Front" back to {blade: 2, side: "front"}
                const surfaceMatch = surface.surface.match(/Blade\s+(\d+)\s+(Front|Back)/i);
                if (surfaceMatch) {
                  const blade = parseInt(surfaceMatch[1], 10);
                  const side = surfaceMatch[2].toLowerCase();
                  engravings.push({
                    blade: blade,
                    side: side,
                    text: surface.text || '',
                    font: this.defaultFont
                  });
                }
              }
            }
          }
          return engravings;
        }
      } catch (error) {
        console.error('Failed to parse JSON_ENGRAVING:', error);
      }
    }

    // Fall back to _engraving_data format
    const raw =
      properties._engraving_data !== undefined
        ? properties._engraving_data
        : properties.engravings;

    if (!raw) {
      return [];
    }

    try {
      const engravings =
        typeof raw === 'string' ? JSON.parse(raw) : raw;
      
      return Array.isArray(engravings) ? engravings : [];
    } catch (error) {
      console.error('Failed to parse engravings:', error);
      return [];
    }
  }

  /**
   * Add engravings to a cart line item
   * @param {string} lineItemKey - Cart line item key
   * @param {Array} engravings - Array of engraving objects
   * @param {number} quantity - Item quantity
   * @returns {Promise<boolean>} Success status
   */
  async addEngravingsToLineItem(lineItemKey, engravings, quantity) {
    try {
      // Validate all engravings
      for (const engraving of engravings) {
        const validation = this.validateEngraving(engraving);
        if (!validation.valid) {
          throw new Error(validation.error);
        }
      }

      // Get current cart to find the line item and get SKU
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      const lineItem = cart.items.find(item => item.key === lineItemKey);
      
      if (!lineItem) {
        throw new Error('Line item not found');
      }

      // Format engravings
      const formattedEngravings = engravings.map((e) =>
        this.formatEngraving(e)
      );

      // Convert to surfaces format for JSON_ENGRAVING
      const surfaces = formattedEngravings.map(engraving => {
        const sideCapitalized = engraving.side.charAt(0).toUpperCase() + engraving.side.slice(1);
        const surface = `Blade ${engraving.blade} ${sideCapitalized}`;
        
        return {
          surface: surface,
          text: engraving.text
        };
      });

      // Create JSON_ENGRAVING structure
      const jsonEngraving = [{
        sku: lineItem.sku || '',
        quantity: quantity,
        surfaces: surfaces
      }];

      // Create a human‑readable summary for checkout / order view
      const summaryText = formattedEngravings
        .map((e) => {
          const side =
            e.side.charAt(0).toUpperCase() + e.side.slice(1);
          return `Blade ${e.blade} ${side}: "${e.text}" (${e.font})`;
        })
        .join(' | ');

      // Update line item properties with JSON_ENGRAVING format
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: lineItemKey,
          properties: {
            // Store JSON_ENGRAVING format (hidden - keys starting with "_" are hidden in checkout)
            JSON_ENGRAVING:JSON.stringify(jsonEngraving),
            // Hidden JSON store for theme/JS (keys starting with "_" are hidden in checkout) - keep for backward compatibility
            _engraving_data: JSON.stringify(formattedEngravings),
            // Human‑readable summary shown in checkout
            Engravings: summaryText
          },
          quantity: quantity
        })
      });

      if (!response.ok) {
        const error = await response.text();
        throw new Error(`Failed to update cart: ${error}`);
      }

      // Sync engraving service product
      await this.syncEngravingServiceProduct();

      return true;
    } catch (error) {
      console.error('Failed to add engravings:', error);
      throw error;
    }
  }

  /**
   * Remove engravings from a cart line item
   * @param {string} lineItemKey - Cart line item key
   * @param {Array} engravingsToRemove - Array of engraving objects to remove
   * @param {number} quantity - Item quantity
   * @returns {Promise<boolean>} Success status
   */
  async removeEngravingsFromLineItem(lineItemKey, engravingsToRemove, quantity) {
    try {
      // Get current cart
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      // Find the line item
      const lineItem = cart.items.find(item => item.key === lineItemKey);
      if (!lineItem) {
        throw new Error('Line item not found');
      }

      // Get current engravings
      const currentEngravings = this.parseEngravingsFromProperties(lineItem.properties);

      // Remove specified engravings
      const remainingEngravings = currentEngravings.filter(current => {
        return !engravingsToRemove.some(toRemove => 
          toRemove.blade === current.blade && 
          toRemove.side === current.side &&
          toRemove.text === current.text
        );
      });

      // Update line item
      let propertiesPayload = {};
      if (remainingEngravings.length > 0) {
        // Convert remaining engravings to surfaces format for JSON_ENGRAVING
        const surfaces = remainingEngravings.map(engraving => {
          const sideCapitalized = engraving.side.charAt(0).toUpperCase() + engraving.side.slice(1);
          const surface = `Blade ${engraving.blade} ${sideCapitalized}`;
          
          return {
            surface: surface,
            text: engraving.text
          };
        });

        // Create JSON_ENGRAVING structure
        const jsonEngraving = [{
          sku: lineItem.sku || '',
          quantity: quantity,
          surfaces: surfaces
        }];

        const summaryText = remainingEngravings
          .map((e) => {
            const side = e.side.charAt(0).toUpperCase() + e.side.slice(1);
            return `Blade ${e.blade} ${side}: "${e.text}" (${e.font || 'Bodoni MT'})`;
          })
          .join(' | ');

        propertiesPayload = {
          // Store JSON_ENGRAVING format (hidden - keys starting with "_" are hidden in checkout)
          JSON_ENGRAVING:JSON.stringify(jsonEngraving),
          // Hidden JSON store for theme/JS (keys starting with "_" are hidden in checkout) - keep for backward compatibility
          _engraving_data: JSON.stringify(remainingEngravings),
          // Human‑readable summary shown in checkout
          Engravings: summaryText
        };
      }

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: lineItemKey,
          properties: propertiesPayload,
          quantity: quantity
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update cart');
      }

      // Sync engraving service product
      await this.syncEngravingServiceProduct();

      return true;
    } catch (error) {
      console.error('Failed to remove engravings:', error);
      throw error;
    }
  }

  /**
   * Calculate total engravings across all cart items
   * @returns {Promise<number>} Total number of engravings
   */
  async calculateTotalEngravings() {
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      let total = 0;

      for (const item of cart.items) {
        // Skip engraving service product
        if (this.isEngravingServiceProduct(item)) {
          continue;
        }

        const engravings = this.parseEngravingsFromProperties(item.properties);
        // Each unit has engravings.length engravings; multiply by quantity
        total += engravings.length * (item.quantity || 1);
      }

      return total;
    } catch (error) {
      console.error('Failed to calculate total engravings:', error);
      return 0;
    }
  }

  /**
   * Check if an item is the engraving service product
   * @param {Object} item - Cart item
   * @returns {boolean}
   */
  isEngravingServiceProduct(item) {
    if (!this.engravingServiceProduct || !item) {
      return false;
    }

    const service = this.engravingServiceProduct;

    // Prefer explicit numeric ID if provided
    let serviceVariantIdNumeric = service.variant_id_numeric;

    // Fallback: derive numeric ID from GID or raw value
    if (!serviceVariantIdNumeric && service.variant_id) {
      if (typeof service.variant_id === 'string' && service.variant_id.indexOf('/') !== -1) {
        const parts = service.variant_id.split('/');
        serviceVariantIdNumeric = parseInt(parts[parts.length - 1], 10);
      } else {
        serviceVariantIdNumeric = parseInt(service.variant_id, 10);
      }
    }

    return (
      (serviceVariantIdNumeric && item.variant_id === serviceVariantIdNumeric) ||
      (service.sku && item.sku === service.sku) ||
      (service.product_id && item.product_id === service.product_id)
    );
  }

  /**
   * Sync engraving service product quantity
   * @returns {Promise<boolean>} Success status
   */
  async syncEngravingServiceProduct() {
    try {
      if (!this.engravingServiceProduct) {
        await this.loadEngravingServiceProduct();
        if (!this.engravingServiceProduct) {
          console.warn('Engraving service product not available');
          return false;
        }
      }

      const totalEngravings = await this.calculateTotalEngravings();

      // Get current cart
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();

      // Find existing service product
      const serviceItem = cart.items.find(item => 
        this.isEngravingServiceProduct(item)
      );

      if (totalEngravings === 0) {
        // Remove service product if no engravings
        if (serviceItem) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              id: serviceItem.key,
              quantity: 0
            })
          });
        }
        return true;
      }

      if (serviceItem) {
        // Update existing service product quantity
        if (serviceItem.quantity !== totalEngravings) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              id: serviceItem.key,
              quantity: totalEngravings
            })
          });
        }
      } else {
        // Add service product using numeric variant ID
        let serviceVariantIdNumeric = this.engravingServiceProduct.variant_id_numeric;
        if (!serviceVariantIdNumeric && this.engravingServiceProduct.variant_id) {
          if (typeof this.engravingServiceProduct.variant_id === 'string' && this.engravingServiceProduct.variant_id.indexOf('/') !== -1) {
            const parts = this.engravingServiceProduct.variant_id.split('/');
            serviceVariantIdNumeric = parseInt(parts[parts.length - 1], 10);
          } else {
            serviceVariantIdNumeric = parseInt(this.engravingServiceProduct.variant_id, 10);
          }
        }

        const payload = {
          id: serviceVariantIdNumeric,
          quantity: totalEngravings
        };

        console.log('🛒 Engraving service add attempt:', payload);

        const addResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        if (!addResponse.ok) {
          const errorText = await addResponse.text().catch(() => '');
          console.error('❌ Failed to add engraving service product:', {
            status: addResponse.status,
            statusText: addResponse.statusText,
            body: errorText
          });
        }
      }

      return true;
    } catch (error) {
      console.error('Failed to sync engraving service product:', error);
      return false;
    }
  }

  /**
   * Get engraving service product info
   * @returns {Object|null} Service product info
   */
  getServiceProductInfo() {
    return this.engravingServiceProduct;
  }

  /**
   * Convert engraving data to JSON_ENGRAVING format
   * @param {Object|Array} cartItems - Single cart item or array of cart items
   * @returns {Object} JSON_ENGRAVING structure
   */
  convertToJSONEngraving(cartItems) {
    // Normalize input to array
    const items = Array.isArray(cartItems) ? cartItems : [cartItems];
    
    const jsonEngraving = [];

    for (const item of items) {
      // Skip engraving service product
      if (this.isEngravingServiceProduct(item)) {
        continue;
      }

      // Get engravings from item properties
      const engravings = this.parseEngravingsFromProperties(item.properties);
      
      // Skip if no engravings
      if (!engravings || engravings.length === 0) {
        continue;
      }

      // Get SKU and quantity
      const sku = item.sku || '';
      const quantity = item.quantity || 1;

      // Convert engravings to surfaces format
      const surfaces = engravings.map(engraving => {
        // Format surface name: "Blade {blade} {side}" where side is capitalized
        const sideCapitalized = engraving.side.charAt(0).toUpperCase() + engraving.side.slice(1);
        const surface = `Blade ${engraving.blade} ${sideCapitalized}`;
        
        return {
          surface: surface,
          text: engraving.text
        };
      });

      // Add to JSON_ENGRAVING array
      jsonEngraving.push({
        sku: sku,
        quantity: quantity,
        surfaces: surfaces
      });
    }

    return {
      JSON_ENGRAVING:jsonEngraving
    };
  }

  /**
   * Get JSON_ENGRAVING from current cart
   * @returns {Promise<Object>} JSON_ENGRAVING structure
   */
  async getJSONEngravingFromCart() {
    try {
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      return this.convertToJSONEngraving(cart.items);
    } catch (error) {
      console.error('Failed to get JSON_ENGRAVING from cart:', error);
      return { JSON_ENGRAVING:[] };
    }
  }
};

window.engravingCore = window.engravingCore || new window.EngravingCore();

